import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Holidays from 'date-holidays';

import { CheckIcon,
  CommonGlobalService,
  CommonUtilsService,
  CrossIcon,
  dayjs,
  FORMAT_DATE_API,
  MissingDay,
  MultiSelect,
  Option,
  RootState,
  UserUmanforge,
  UUID,
  ListPaginationResult } from '@umanforge/common';
import { PostMissingDayToUser } from 'interfaces/api_missing_day.interfaces';
import { ModifyHolidayLocation } from 'interfaces/api_holiday.interfaces';

import { ToastService } from 'services/toast.service';
import { UserService } from 'services/user.service';
import { MissingDayService } from 'services/missing_day.service';

import { ROUTES, USER_ID } from 'constants/navigation.constants';

import { Button } from 'components/inputs/button.component';
import SelectInput, { Option as OldOption, GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import DatePickerInput from '@common_components/inputs/datePickerInput.component';
import Pagination from '@common_components/inputs/selects/pagination.component';

import { SaveIcon } from 'components/icons/save_icon.component';
import { TrashIcon } from '@common_components/icons/trash.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { UserIcon } from 'components/icons/user_icon.component';
import { UsersIcon } from 'components/icons/users_icon.component';
import { Breadcrumb } from 'components/menu/breadcrumb.component';

interface AbsenceUserProps {
  user: UserUmanforge;
  refreshUser: () => Promise<void>;
  needConfirmation: boolean;
  setNeedConfirmation: Dispatch<SetStateAction<boolean>>;
}

const ADDED_MISSINGDAY_ID = 'added_MissingDay';

export function AbsenceUserComponent( { user, refreshUser, needConfirmation, setNeedConfirmation }: AbsenceUserProps ) {
  const toast = new ToastService( 'AbsenceUserComponent' );
  const { t } = useTranslation();

  const params = useParams();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  const holidays = new Holidays();

  /*----------------------------- useState ------------------------------*/
  const [ usePublicHolidays, setUsePublicHolydays ] = useState( false );
  const [ canPut, setCanPut ] = useState( false );

  const [ selectedHolidays, setSelectedHolidays ] = useState<{ name: string, id: string }[]>( [] );

  const [ missingDays, setMissingDays ] = useState<{ total: number; datas: MissingDay[]; }>( { total: 0, datas: [] } );
  const [ addedMissingDays, setAddedMissingDays ] = useState<MissingDay[]>( [] );
  const [ removedMissingDays, setRemovedMissingDays ] = useState<UUID[]>( [] );

  const [ countries, setCountries ] = useState<GroupedOption[]>( [] );
  const [ selectedCountry, setSelectedCountry ] = useState<string | null>();
  const [ states, setStates ] = useState<GroupedOption[]>( [] );
  const [ selectedState, setSelectedState ] = useState<string | null>();
  const [ regions, setRegions ] = useState<GroupedOption[]>( [] );
  const [ selectedRegion, setSelectedRegion ] = useState<string | null>();

  const [ page, setPage ] = useState<number>( 1 );
  const [ totalPages, setTotalPages ] = useState<number>( 1 );
  const [ step ] = useState<number>( 5 );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ selectHolidayKey, setSelectHolidayKey ] = useState<number>( 0 );

  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    initStates();
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshMissingDays();
    }
  }, [ isFirstRender, user, page, step ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedState( null );
      if ( selectedCountry ) {
        setStates( objectToGroupedOption( holidays.getStates( selectedCountry ) ) );
      } else {
        setStates( [] );
      }
    }
  }, [ selectedCountry ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedRegion( null );
      if ( selectedCountry && selectedState ) {
        setRegions( objectToGroupedOption( holidays.getRegions( selectedCountry, selectedState ) ) );
      } else {
        setRegions( [] );
      }
    }
  }, [ selectedState ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      updateHolidays();
    }
  }, [ selectedCountry, selectedState, selectedRegion ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      checkCanPut();
    }
  }, [ selectedCountry, selectedState, selectedRegion, selectedHolidays, usePublicHolidays, removedMissingDays, addedMissingDays ] );

  useEffect( () => {
    updateHolidays();
  }, [ isFirstRender ] );

  /*------------------------- Functions -------------------------*/
  const initStates = () => {
    setUsePublicHolydays( user.holidays != null );
    const contriesUpdates = objectToGroupedOption( holidays.getCountries() );
    setCountries( contriesUpdates );
    if ( user.holidays ) {
      const { country, state, region } = user.holidays;
      setSelectedCountry( country );
      const statesUpdated = objectToGroupedOption( holidays.getStates( country ) );
      setStates( statesUpdated );
      setSelectedState( state );
      setSelectedState( state != '' ? state : null );
      if ( state ) {
        const regionsUpdated = objectToGroupedOption( holidays.getRegions( country, state ) );
        setRegions( regionsUpdated );
        setSelectedRegion( region != '' ? region : null );
      }
    } else {
      setSelectedHolidays( [] );
      rerenderSelectHoliday();
    }
    setTimeout( () => { setIsFirstRender( false ); }, 75 );
  };

  const refreshMissingDays = async () => {
    const updatedClosedDays: { total: number, datas: MissingDay[] } = await MissingDayService.getMissingDaysByUser( user.id, page, step, token, dispatch );
    setMissingDays( updatedClosedDays );
    setTotalPages( Math.ceil( updatedClosedDays.total / step ) );
    if ( isFirstRender ) {
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  };

  const updateHolidays = () => {
    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );

    setSelectedHolidays(
      holidayInstance.getHolidays( dayjs().year() )
        .filter( holiday => {
          if ( !( user.holidays?.country == selectedCountry ) ) {
            return true;
          }
          return user.holidays && user.holidays.excludedRules.indexOf( holiday.rule ) === -1;
        } )
        .sort( ( item1, item2 ) => { return dayjs( item1.start ).isAfter( dayjs( item2.start ) ) ? 1 : -1;  } )
        .map( holiday => { return {  name: holiday.name, id: holiday.rule }; } )
    );
    rerenderSelectHoliday();
  };

  const isValidMissingDaysForUser = async ( dates: PostMissingDayToUser ) => {
    let isValid = true;
    if ( user ) {
      const formatedDates = [ { startDate: dayjs( dates.startDate ), endDate: dayjs( dates.endDate ) } ];
      isValid = isValid && await MissingDayService.isValidMissingDaysForUser( user.id, formatedDates, removedMissingDays, token, dispatch );
    }

    const otherMissingDays = getRelevantMissingDays();
    isValid = isValid && otherMissingDays.find( missingDay => {
      const startMissingDay = dayjs( missingDay.startDate );
      const endMissingDay = dayjs( missingDay.endDate );
      const startDate = dayjs( dates.startDate );
      const endDate = dayjs( dates.endDate );
      return !( startMissingDay.isAfter( endDate ) || endMissingDay.isBefore( startDate ) );
    } ) == null;
    return isValid;
  };

  const checkCanPut = () => {
    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );

    const allHolidays = holidayInstance.getHolidays( dayjs().year() )
      .map( holiday => { return holiday.rule; } )
      .sort();

    const userExcludedHolidays: string[] = [ ...( user.holidays?.excludedRules ?? [] ) ]
      .sort();
    const selectedExcludedHolidays: string[] = allHolidays
      .filter( selectedHoliday => { return selectedHolidays.find( holiday => { return selectedHoliday == holiday.id; } ) == null; } )
      .sort();

    const isHolidaysChange = (
      (
        usePublicHolidays == true && selectedCountry != null &&
        (
          ( selectedCountry != '' && selectedCountry != user.holidays?.country ) ||
          selectedState != user.holidays?.state ||
          selectedRegion != user.holidays?.region ||
          ( selectedExcludedHolidays.toString() !== userExcludedHolidays.toString() )
        )
      ) ||
      ( usePublicHolidays == false && ( ( user.holidays != null ) != usePublicHolidays ) )
    );

    const isMissingDaysChange = (
      addedMissingDays.length > 0 || removedMissingDays.length > 0
    );

    setCanPut( isHolidaysChange || isMissingDaysChange );
    setNeedConfirmation( isHolidaysChange || isMissingDaysChange );
  };

  const rerenderSelectHoliday = () => {
    setSelectHolidayKey( selectHolidayKey + 1 );
  };

  const getRelevantMissingDays = (): MissingDay[] => {
    const persistedMissingDays = missingDays.datas.filter( missingDay => {
      return !removedMissingDays.includes( missingDay.id );
    } );
    return [ ...persistedMissingDays, ...addedMissingDays ];
  };

  const objectToGroupedOption = ( object: { [ key: string ]: string } ): GroupedOption[] => {
    const result: GroupedOption[] = [];
    if ( object ) {
      const options: OldOption[] = [];
      Object.keys( object ).map( key => {
        options.push( {
          label: object[ key ],
          id: key
        } );
      } );
      result.push( { options: CommonUtilsService.sortByKey( options, 'label', 'asc' ) } );
    }
    return result;
  };

  const getPromiseHolidays = ( country?: string, state?: string, region?: string ): Promise<ListPaginationResult<{ name: string, id: string }>> => {
    return new Promise( resolve => {
      const holidayInstance: Holidays = new Holidays();
      holidayInstance.init( country, state ?? undefined, region ?? undefined, { types: [ 'public' ] } );

      const allowedHolidays: Option<{ name: string, id: string }>[] = CommonGlobalService.toOptions(
        holidayInstance.getHolidays( dayjs().year() )
          .sort( ( item1, item2 ) => { return dayjs( item1.start ).isAfter( dayjs( item2.start ) ) ? 1 : -1;  } )
          .map( holiday => { return { name: holiday.name, id: holiday.rule }; } )
      );
      resolve( { total: allowedHolidays.length, list: allowedHolidays } );
    } );
  };


  /*------------------------- Services -------------------------*/
  const putHolidayLocation = async (): Promise<boolean> => {
    let isUpdate = false;
    if (
      usePublicHolidays == true && selectedCountry != null &&
      ( ( selectedCountry != '' && selectedCountry != user.holidays?.country ) || selectedState != user.holidays?.state || selectedRegion != user.holidays?.region )
    ) {
      const body: ModifyHolidayLocation = {
        country: selectedCountry ?? undefined,
        state: selectedState != '' ? ( selectedState ?? undefined ) : undefined,
        region: selectedRegion != '' ? ( selectedRegion ?? undefined ) : undefined
      };
      isUpdate = await UserService.putHolidayLocationOfUser( user.id, body, token, dispatch );
    }
    return isUpdate;
  };

  const deleteHolidayLocation = async (): Promise<boolean> => {
    let isDelete = false;
    if ( usePublicHolidays == false && ( ( user.holidays != null ) != usePublicHolidays ) ) {
      isDelete = await UserService.deleteHolidayLocationOfUser( user.id, token, dispatch );
    }
    return isDelete;
  };

  const postAndDeleteHolidays = async (): Promise<boolean> => {
    let isAdded = false;
    let isRemoved = false;

    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );

    const allHolidays = holidayInstance.getHolidays( dayjs().year() )
      .map( holiday => { return holiday.rule; } )
      .sort();

    const userExcludedHolidays: string[] = [ ...( user.holidays?.excludedRules ?? [] ) ]
      .sort();
    const selectedExcludedHolidays: string[] = allHolidays
      .filter( selectedHoliday => { return selectedHolidays.find( holiday => { return selectedHoliday == holiday.id; } ) == null; } )
      .sort();

    if (
      usePublicHolidays == true &&
      ( selectedExcludedHolidays.toString() !== userExcludedHolidays.toString() )
    ) {

      const addededRules: string[] = selectedExcludedHolidays.filter( selectedRule => {
        return userExcludedHolidays.find( userRule => { return userRule === selectedRule; } ) == null;
      } );
      const removedRules: string[] = userExcludedHolidays.filter( userRule => {
        return selectedExcludedHolidays.find( selectedRule => { return selectedRule === userRule; } ) == null;
      } );

      if ( addededRules.length > 0 ) {
        isAdded = await UserService.postExcludedHolidaysToUser( user.id, addededRules, token, dispatch );
      }
      if ( removedRules.length > 0 ) {
        isRemoved = await UserService.removedExcludedHolidaysToUser( user.id, removedRules, token, dispatch );
      }

    }
    return isAdded || isRemoved;
  };

  const postMissingDays = async (): Promise<boolean> => {
    let isUpdate = false;
    if ( addedMissingDays.length > 0 ) {
      const body: PostMissingDayToUser[] = addedMissingDays.map( addedMissingDay => {
        return {
          startDate: dayjs( addedMissingDay.startDate ).format( FORMAT_DATE_API ),
          endDate: dayjs( addedMissingDay.endDate ).format( FORMAT_DATE_API )
        };
      } );
      isUpdate = await UserService.postMissingDaysOfUser( user.id, body, token, dispatch );
    }
    return isUpdate;
  };

  const deleteMissingDays = async (): Promise<boolean> => {
    let isDelete = false;
    if ( removedMissingDays.length > 0 ) {
      isDelete = await UserService.deleteMissingDaysOfUser( removedMissingDays, token, dispatch );
    }
    return isDelete;
  };

  /*------------------------- Events -------------------------*/
  const onChangeCountry = ( country: string ) => {
    if ( country !== selectedCountry ) {
      setSelectedCountry( country );
    } else {
      setSelectedCountry( null );
    }
  };

  const onChangeState = ( state: string ) => {
    if ( state !== selectedState ) {
      setSelectedState( state != '' ? state : null );
    } else {
      setSelectedState( null );
    }
  };

  const onChangeRegion = ( region: string ) => {
    if ( region !== selectedRegion ) {
      setSelectedRegion( region != '' ? region : null );
    } else {
      setSelectedRegion( null );
    }
  };

  const onSaveClick = async () => {
    let isUpdate = false;
    if ( usePublicHolidays ) {
      isUpdate = await putHolidayLocation() || isUpdate;
      isUpdate = await postAndDeleteHolidays() || isUpdate;
    } else {
      isUpdate = await deleteHolidayLocation() || isUpdate;
    }
    isUpdate = await deleteMissingDays() || isUpdate;
    isUpdate = await postMissingDays() || isUpdate;

    if ( isUpdate ) {
      toast.showInfo( t( 'Absence days updated !' ) );
      refreshUser();
    }
  };

  const onDeleteMissingDay = ( missingDay: MissingDay ) => {
    if ( missingDay.id != ADDED_MISSINGDAY_ID ) {
      setRemovedMissingDays( [ ...removedMissingDays, missingDay.id ] );
    } else {
      const index = addedMissingDays.indexOf( missingDay );
      if ( index != -1 ) {
        const nextAddedMissingDays = [ ...addedMissingDays ];
        nextAddedMissingDays.splice( index, 1 );
        setAddedMissingDays( nextAddedMissingDays );
      }
    }
  };

  const onUnDeleteMissingDay = async ( missingDay: MissingDay ) => {
    if ( removedMissingDays.includes( missingDay.id ) ) {
      const isValideMissingDay = await isValidMissingDaysForUser( missingDay );
      if  ( isValideMissingDay ) {
        const index = removedMissingDays.indexOf( missingDay.id );
        if ( index != -1 ) {
          const nextRemovedMissingDays = [ ...removedMissingDays ];
          nextRemovedMissingDays.splice( index, 1 );
          setRemovedMissingDays( nextRemovedMissingDays );
        }
      } else {
        toast.showWarning( t( 'An absence has already occurred during this period.' ) );
      }
    }
  };

  const onAddMissingDay = async ( dates: PostMissingDayToUser ) => {
    const isValideClosedDay = await isValidMissingDaysForUser( dates );
    if ( isValideClosedDay ) {
      if ( me ) {
        const nextAddedMissingDays = [
          ...addedMissingDays,
          {
            id: ADDED_MISSINGDAY_ID,
            startDate: dates.startDate,
            endDate: dates.endDate,
            userAdded: me?.fullname,
            registerDate: dayjs().format( FORMAT_DATE_API )
          }
        ];
        setAddedMissingDays( nextAddedMissingDays.sort( ( item1, item2 ) => { return dayjs( item1.startDate ).isAfter( dayjs( item2.startDate ) ) ? -1 : 1; } ) );
      }
    } else {
      toast.showWarning( t( 'An absence has already occurred during this period.' ) );
    }
  };


  /*------------------------- Front --------------------------*/
  const hasAnyAbsenceRows = missingDays.total > 0 || addedMissingDays.length > 0;

  return (
    <>
      <Breadcrumb
        needConfirmationToNav={ needConfirmation }
        links={
          [
            { label: t( 'common:user' ), route: ROUTES.USERS, icon: ( <UsersIcon className='h-[14px] select-none cursor-pointer'/> ) },
            { label: user ? user.fullname : t( 'Modify user' ), route: ROUTES.USER.replace( USER_ID, params.userId ?? '' ), icon: ( <UserIcon className='h-[14px] select-none cursor-pointer'/> ) }
          ]
        }
      />
      <div className='relative flex flex-col h-full w-full items-center justify-center'>
        <div className='flex flex-col w-full h-full items-center max-w-[1500px] min-w-[400px] gap-10 px-10 mb-[63px] mt-4'>

          {
            !user.isAdminOfUser && !usePublicHolidays ?
              null
              :
              <div className='flex flex-col w-full p-6 rounded-lg'>
                {
                  user.isAdminOfUser ?
                    <div className='-mx-[8px]'>
                      <Button
                        label={ t( 'common:add_public_holidays' ) }
                        color={ usePublicHolidays ? '' : '#f87171' }
                        colorHover={ usePublicHolidays ? '' : '#ef4444' }
                        icon={ usePublicHolidays ? <CheckIcon className='h-7 w-7 text-white'/> : <CrossIcon className='h-7 w-7 text-white'/> }
                        onClick={ () => { setUsePublicHolydays( !usePublicHolidays ); } }
                      />
                    </div>
                    :
                    null
                }
                <div
                  key={ selectHolidayKey }
                  className={`w-full origin-top-left transition-all duration-500 ${usePublicHolidays ? 'scale-100 h-100' : 'scale-0 invisible h-0'}`}
                >
                  {
                    usePublicHolidays ?
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-end gap-2 w-full'>
                          <div className='w-1/3'>
                            <SelectInput
                              isDisable={!user.isAdminOfUser}
                              isRequired
                              emptyGroupsMessage={ t( 'common:no_countries_available' ) }
                              placeholder={ t( 'common:select_a_country' ) }
                              label={ t( 'common:country' ) }
                              fetchOptions={ () => { return Promise.resolve( countries ); } }
                              selected={ selectedCountry ?? undefined }
                              labelPosition='top'
                              onSelect={ onChangeCountry }
                            />
                          </div>
                          <div className='w-1/3'>
                            <SelectInput
                              emptyGroupsMessage={ t( 'common:no_states_available' ) }
                              placeholder={ t( 'common:select_a_state' ) }
                              label={ t( 'common:state' ) }
                              fetchOptions={ () => { return Promise.resolve( states ); } }
                              isDisable={ !selectedCountry || !user.isAdminOfUser }
                              selected={ selectedState ?? undefined }
                              labelPosition='top'
                              onSelect={ onChangeState }
                            />
                          </div>
                          <div className='w-1/3'>
                            <SelectInput
                              emptyGroupsMessage={ t( 'common:no_regions_available' ) }
                              placeholder={ t( 'common:select_a_region' ) }
                              label={ t( 'common:region' ) }
                              fetchOptions={ () => { return Promise.resolve( regions ); } }
                              isDisable={ !selectedState || !user.isAdminOfUser }
                              selected={ selectedRegion ?? undefined }
                              labelPosition='top'
                              onSelect={ onChangeRegion }
                            />
                          </div>
                        </div>
                        <div className='w-full'>
                          <MultiSelect
                            label={ t( 'common:public_holidays' ) }
                            getValues={ () => { return getPromiseHolidays( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined ); } }
                            selectedValues={ CommonGlobalService.toOptions( selectedHolidays ) }
                            onChange={ values => { setSelectedHolidays( values.map( value => { return value.object; } ) ); } }
                          />
                        </div>
                      </div>
                      :
                      null
                  }
                </div>
              </div>
          }

          <div className='flex flex-col h-full w-full p-6 gap-4 rounded-lg'>
            {
              user.isAdminOfUser ?
                <DatePickerInput
                  label={ t( 'common:plan_an_absence' ) }
                  labelPosition='top'
                  textSize='w-full'
                  closeAfterSelect
                  clearAfrerClose
                  onChange={ ( start, end ) => {
                    onAddMissingDay( {
                      startDate: dayjs( start ).format( FORMAT_DATE_API ),
                      endDate: dayjs( end ).format( FORMAT_DATE_API )
                    } );
                  } }
                />
                :
                null
            }
            <div
              className='rounded-lg border-[1px] border-gray-200 overflow-hidden'
              key={ addedMissingDays.length + removedMissingDays.length }
            >
              <table className='w-full text-left gap-1'>
                <thead>
                  <tr className='text-[16px] text-gray-500 border-b-2 border-gray-300'>
                    <th className='px-4 py-2 text-gray-600 font-normal tracking-wider'> { t( 'Date(s)' ) } </th>
                    <th className='px-4 py-2 text-gray-600 font-normal tracking-wider'> { t( 'Date added' ) } </th>
                    <th className='px-4 py-2 text-gray-600 font-normal tracking-wider'> { t( 'By' ) } </th>
                    {
                      user.isAdminOfUser ?
                        <th></th>
                        :
                        null
                    }
                  </tr>
                </thead>
                <>
                  {
                    addedMissingDays.length > 0 ?
                      <tbody className='border-b-2 border-gray-300'>
                        {
                          addedMissingDays.map( ( missingday, index ) => {
                            const isDeleted = removedMissingDays.some( id => { return missingday.id === id; } );
                            const startDate = dayjs( missingday.startDate );
                            const registerDate = dayjs( missingday.registerDate );
                            const endDate = dayjs( missingday.endDate );
                            const displayRange = startDate.isSame( endDate, 'day' ) ? startDate.format( 'LL' ) : ( startDate.format( 'LL' ) + ' - ' + endDate.format( 'LL' ) );
                            const displayRegisterDate = registerDate.format( 'LL' );
                            return (
                              <tr
                                key={ missingday.id + startDate + endDate }
                                className={
                                  (
                                    isDeleted ?
                                      ( index % 2 === 1 ? 'bg-red-200' : 'bg-red-100' )
                                      :
                                      ( index % 2 === 1 ? 'bg-sky-100' : 'bg-sky-50' )
                                  )
                                }
                              >
                                <td className='px-4 py-3'>
                                  { displayRange }
                                  <span className="h-fit w-fit align-text-top bg-blue-200 text-blue-800 text-[10px] font-medium ml-2 px-1.5 py-0.5 rounded-md">
                                    { t( 'common:new' ) }
                                  </span>
                                </td>
                                <td className='px-4 py-3'> { displayRegisterDate } </td>
                                <td className='px-4 py-3'> { missingday.userAdded } </td>
                                {
                                  isDeleted ?
                                    <td className='px-2 justify-items-center'>
                                      <PlusIcon
                                        size={ 7 }
                                        className='h-7 w-7 cursor-pointer transition ease-in-out delay-75 hover:text-green-500 hover:scale-105 active:scale-95'
                                        onClick={ () => { onAddMissingDay( missingday ); } }
                                      />
                                    </td>
                                    :
                                    <td className='px-2 justify-items-center'>
                                      <TrashIcon
                                        size={ 7 }
                                        className='h-7 w-7 cursor-pointer transition ease-in-out delay-75 hover:text-red-500 hover:scale-105 active:scale-95'
                                        onClick={ () => { onDeleteMissingDay( missingday ); } }
                                      />
                                    </td>
                                }
                              </tr>
                            );
                          } )
                        }
                      </tbody>
                      :
                      null
                  }
                  {
                    missingDays.total > 0 ?
                      <tbody>
                        {
                          missingDays.datas.map( ( missingday, index ) => {
                            const isDeleted = removedMissingDays.some( id => { return missingday.id === id; } );
                            const startDate = dayjs( missingday.startDate );
                            const endDate = dayjs( missingday.endDate );
                            const registerDate = dayjs( missingday.registerDate );
                            const displayRange = startDate.isSame( endDate, 'day' ) ? startDate.format( 'LL' ) : ( startDate.format( 'LL' ) + ' - ' + endDate.format( 'LL' ) );
                            const displayRegisterDate = registerDate.format( 'LL' );
                            return (
                              <tr
                                key={ missingday.id + startDate + endDate }
                                className={
                                  (
                                    isDeleted ?
                                      ( index % 2 === 1 ? 'bg-red-200' : 'bg-red-100' )
                                      :
                                      ( index % 2 === 1 ? 'bg-gray-base-light' : 'bg-white' )
                                  )
                                }
                              >
                                <td className='px-4 py-3'>
                                  { displayRange }
                                </td>
                                <td className='px-4 py-3'> { displayRegisterDate } </td>
                                <td className='px-4 py-3'> { missingday.userAdded } </td>
                                {
                                  user.isAdminOfUser ?
                                    <>
                                      {
                                        isDeleted ?
                                          <td className='px-2 justify-items-center'>
                                            <PlusIcon
                                              size={ 7 }
                                              className='h-7 w-7 cursor-pointer transition ease-in-out delay-75 hover:text-green-500 hover:scale-105 active:scale-95'
                                              onClick={ () => { onUnDeleteMissingDay( missingday ); } }
                                            />
                                          </td>
                                          :
                                          <td className='px-2 justify-items-center'>
                                            <TrashIcon
                                              size={ 7 }
                                              className='h-7 w-7 cursor-pointer transition ease-in-out delay-75 hover:text-red-500 hover:scale-105 active:scale-95'
                                              onClick={ () => { onDeleteMissingDay( missingday ); } }
                                            />
                                          </td>
                                      }
                                    </>
                                    :
                                    null
                                }
                              </tr>
                            );
                          } )
                        }
                      </tbody>
                      :
                      null
                  }
                </>
              </table>
              {
                hasAnyAbsenceRows ?
                  null
                  :
                  <div className='flex w-full py-2 bg-gray-50 items-center justify-center'>
                    { t( 'No days of absence recorded' ) }
                  </div>
              }
            </div>
            {
              totalPages > 1 ?
                <Pagination
                  currentPage={ page }
                  totalPages={ totalPages }
                  onPageChange={ setPage }
                />
                :
                null
            }
          </div>
        </div>

        {
          user.isAdminOfUser ?
            <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
              <Button
                label={ t( 'save' ) }
                disable={ !canPut }
                icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                color='#0bb173'
                colorHover='#088D5C'
                onClick={ onSaveClick }
              />
            </div>
            :
            null
        }
      </div>
    </>
  );
}